import { useCreateReservation, useReservation } from "api/api-hooks/use-create-reservation";
import { useBookingId } from "hooks/use-booking-id";
import { LayoutContext } from "layout/layout-context";
import { sendToParent, trackEvent, error as logError } from "logger/logger";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Routes } from "routes";
import { getSearchParams } from "search-params";
import { deriveUrlParamsFromState } from "search-params/url-params";
import { BookingSetStateContext, BookingStateContext } from "store/store-context";
import { getStateFromParams } from "store/store-helper";
import { State } from "store/store-types";
import { Contact } from "types/contact";
import { Customer } from "types/customer";
import { PaymentMethod } from "types/payment";

export const BookingProvider = () => {
    const params = getSearchParams();
    const location = useLocation();
    const layoutContext = useContext(LayoutContext);
    const reservation = useCreateReservation();
    const { data: booking } = useReservation();
    const [state, setState] = useState(getStateFromParams(params, { selectedMonth: new Date() }));
    const navigate = useNavigate();
    const bookingId = useBookingId();

    const guests = params.guests ?? booking?.guests;
    const guestsChildren = params.guestsChildren ?? booking?.guestsChildren ?? null;
    const date = params.date ?? booking?.start.clone().startOf("day");
    const isWaitList = booking?.isWaitList() ?? state.isWaitList;

    const isActivityBooking = location.pathname.includes(Routes.ACTIVITY_BOOKING);

    useEffect(() => {
        const { webBookingId } = getSearchParams();
        if (params.guests || bookingId || webBookingId) layoutContext.setShowCoverImage?.(false);
    }, [bookingId, layoutContext, params.guests]);

    useEffect(() => {
        sendToParent(state);
    }, [state]);

    /**
     * @param updates Partial state object with properties to update
     * @param updateUrl Whether to update the URL with the state changes (default: true)
     */
    const setMultipleStateProps = (updates: Partial<State>) => {
        // Update state immediately
        setState((prevState) => {
            const newState = {
                ...prevState,
                ...updates,
            };

            return newState;
        });

        const urlParams = deriveUrlParamsFromState(updates);
        const newSearch = urlParams.toString();

        const path = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`;
        void navigate(path);
    };

    const setPaymentMethod = (method: PaymentMethod) => {
        setState((prev) => ({
            ...prev,
            payment: { payMethod: method },
        }));
    };

    const setGuests = (guests: number) => {
        trackEvent("GuestsSelected", { count: guests });
        setMultipleStateProps({ guests });
    };

    const setChildren = (guestsChildren: number) => {
        trackEvent("SelectChildrenAmount", { guestsChildren, children: guestsChildren });
        setMultipleStateProps({ guestsChildren });
    };

    const setDate = (date: Date) => {
        const newState = {
            ...state,
            date: moment(date),
        };
        setState(newState);
        const urlParams = deriveUrlParamsFromState({
            date: moment(date),
        });
        const newSearch = urlParams.toString();

        const path = `${window.location.pathname.replace(/\/$/, "")}/${Routes.AVAILABILITY}${newSearch ? `?${newSearch}` : ""}`;
        void navigate(path, { replace: false });
    };

    const setContact = (contact: Partial<Contact>) => {
        setState((prev) => ({
            ...prev,
            contact: {
                ...prev.contact,
                ...contact,
            } as Contact,
        }));
    };

    const setCustomer = (customer: Customer) => {
        setState((prev) => ({
            ...prev,
            customer: {
                ...prev.customer,
                ...customer,
            } as Customer,
        }));
    };

    const handleCreateBooking = async (state: State, toMenu = false) => {
        window.scrollTo({ behavior: "smooth", top: 0, left: 0 });
        window.parent.postMessage("scroll2top", "*");
        setState(state);

        const nextRoute = toMenu ? Routes.PREORDER : Routes.FINALIZE;
        const path = `${location.pathname.replace(Routes.AVAILABILITY, nextRoute)}${location.search}`;
        await navigate(path);

        reservation.mutate(state, {
            onError: (error) => logError("Reservation creation failed", error),
        });
    };

    const storeActions = {
        setState,
        setPaymentMethod,
        setGuests,
        setChildren,
        setDate,
        setContact,
        setCustomer,
        handleCreateBooking,
        reservation,
    };

    return (
        <BookingStateContext.Provider
            value={{ ...state, guests, guestsChildren, date, isActivityBooking, isWaitList }}
        >
            <BookingSetStateContext.Provider value={storeActions}>
                <Outlet />
            </BookingSetStateContext.Provider>
        </BookingStateContext.Provider>
    );
};
